import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ProductOrderCounts {
  d7: number;
  d30: number;
  d90: number;
}

type CountsMap = Record<string, ProductOrderCounts>;

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min
/**
 * Per-product cache — jede Product-ID hat ihren eigenen Eintrag/inflight.
 *
 * WICHTIG: Anonyme Besucher haben KEIN SELECT-Recht auf `orders`/`order_items`
 * (RLS), daher wird der Zähler serverseitig über die SECURITY-DEFINER-RPC
 * `get_product_order_counts` aggregiert. Nur die drei Aggregatzahlen
 * (d7/d30/d90) verlassen die DB — keine PII, keine Order-Daten.
 */
const cache = new Map<string, { fetchedAt: number; data: ProductOrderCounts }>();
const inflight = new Map<string, Promise<ProductOrderCounts>>();
const ZERO: ProductOrderCounts = { d7: 0, d30: 0, d90: 0 };

async function loadCountsFor(productId: string): Promise<ProductOrderCounts> {
  const cached = cache.get(productId);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) return cached.data;
  const existing = inflight.get(productId);
  if (existing) return existing;

  const p = (async () => {
    try {
      const { data, error } = await supabase.rpc('get_product_order_counts', { _product_id: productId });
      const row = Array.isArray(data) ? data[0] : data;
      const counts: ProductOrderCounts = error || !row
        ? { ...ZERO }
        : { d7: Number(row.d7) || 0, d30: Number(row.d30) || 0, d90: Number(row.d90) || 0 };
      cache.set(productId, { fetchedAt: Date.now(), data: counts });
      return counts;
    } finally {
      inflight.delete(productId);
    }
  })();
  inflight.set(productId, p);
  return p;
}

/** Returns 7/30/90-day order counts for a single product. */
export function useProductOrderCounts(productId: string | null | undefined): ProductOrderCounts {
  const [counts, setCounts] = useState<ProductOrderCounts>(() => {
    if (productId && cache.get(productId)) return cache.get(productId)!.data;
    return { ...ZERO };
  });

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    loadCountsFor(productId).then(c => {
      if (cancelled) return;
      setCounts(c);
    });
    return () => { cancelled = true; };
  }, [productId]);

  return counts;
}

/** Returns the full counts map for ALL products (admin / batch use). */
export function useAllProductOrderCounts(): { counts: CountsMap; loading: boolean } {
  // Admin/Batch nutzt weiterhin die Direktabfrage (Admin hat SELECT via RLS-Policy).
  const [counts, setCounts] = useState<CountsMap>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const since90 = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('order_items')
        .select('product_id, created_at, orders!inner(status, created_at)')
        .gte('created_at', since90);
      const map: CountsMap = {};
      if (!error && data) {
        const now = Date.now();
        const d7Cut = now - 7 * 24 * 60 * 60 * 1000;
        const d30Cut = now - 30 * 24 * 60 * 60 * 1000;
        for (const row of data as any[]) {
          const status = row.orders?.status;
          if (status === 'cancelled' || status === 'failed') continue;
          const pid = row.product_id;
          if (!pid) continue;
          const ts = new Date(row.orders?.created_at || row.created_at).getTime();
          const entry = map[pid] || (map[pid] = { d7: 0, d30: 0, d90: 0 });
          entry.d90 += 1;
          if (ts >= d30Cut) entry.d30 += 1;
          if (ts >= d7Cut) entry.d7 += 1;
        }
      }
      if (cancelled) return;
      setCounts(map);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return { counts, loading };
}